import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { createPaymentOrder, fetchPaymentConfig, markPaymentFailed, verifyPayment } from '../../services/paymentService';
import CustomSelect from '../ui/CustomSelect';

function loadRazorpayScript() {
  return new Promise((resolve, reject) => {
    if (window.Razorpay) {
      resolve(true);
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.async = true;
    script.onload = () => resolve(true);
    script.onerror = () => reject(new Error('Unable to load Razorpay checkout.'));
    document.body.appendChild(script);
  });
}

function getInitialFormState(location) {
  const searchParams = new URLSearchParams(location.search);

  return {
    customerName: searchParams.get('customerName') || location.state?.customerName || '',
    email: searchParams.get('email') || location.state?.email || '',
    phone: searchParams.get('phone') || location.state?.phone || '',
    amount: searchParams.get('amount') || location.state?.amount || '',
    purpose: searchParams.get('purpose') || location.state?.purpose || '',
    merchantAccountId: searchParams.get('merchantAccountId') || location.state?.merchantAccountId || ''
  };
}

function FeatureCard({ title, description }) {
  return (
    <div className="rounded-2xl bg-white/12 p-3 backdrop-blur ring-1 ring-white/15 sm:p-4">
      <p className="text-xs font-semibold text-white sm:text-sm">{title}</p>
      <p className="mt-1.5 text-[11px] leading-4 text-white/85 sm:mt-2 sm:text-sm sm:leading-6">
        {description}
      </p>
    </div>
  );
}

function getErrorMessage(error, fallbackMessage) {
  if (!error) {
    return fallbackMessage;
  }

  if (typeof error === 'string') {
    return error || fallbackMessage;
  }

  return error.message || fallbackMessage;
}

function isVisibleMerchantAccount(account) {
  const id = String(account?.id || '').trim().toLowerCase();
  const label = String(account?.label || '').trim().toLowerCase();

  if (!id && !label) {
    return false;
  }

  return id !== 'secondary' && !label.includes('lavish');
}

const PaymentPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [formData, setFormData] = useState(() => getInitialFormState(location));
  const [errors, setErrors] = useState({});
  const [statusMessage, setStatusMessage] = useState('');
  const [statusType, setStatusType] = useState('info');
  const [isLoading, setIsLoading] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isConfigLoading, setIsConfigLoading] = useState(true);
  const [paymentConfig, setPaymentConfig] = useState({
    merchantAccounts: [],
    defaultMerchantAccountId: ''
  });
  const isMountedRef = useRef(true);
  const isProcessingRef = useRef(false);
  const paymentOutcomeHandledRef = useRef(false);
  const activePaymentRecordRef = useRef(null);

  useEffect(() => {
    setFormData(getInitialFormState(location));
  }, [location]);

  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    isProcessingRef.current = isProcessing;
  }, [isProcessing]);

  useEffect(() => {
    let isMounted = true;

    const loadPaymentConfig = async () => {
      try {
        const response = await fetchPaymentConfig();

        if (!isMounted) {
          return;
        }

        const accounts = (response.data?.merchantAccounts || []).filter(isVisibleMerchantAccount);
        const defaultAccountId = response.data?.defaultMerchantAccountId || '';
        const visibleDefaultAccountId =
          accounts.find((account) => account.id === defaultAccountId)?.id ||
          accounts.find((account) => account.isDefault)?.id ||
          accounts[0]?.id ||
          '';

        setPaymentConfig({
          merchantAccounts: accounts,
          defaultMerchantAccountId: visibleDefaultAccountId
        });

        setFormData((previous) => ({
          ...previous,
          merchantAccountId: accounts.some((account) => account.id === previous.merchantAccountId)
            ? previous.merchantAccountId
            : visibleDefaultAccountId
        }));

        if (!accounts.length) {
          setStatusType('error');
          setStatusMessage('No Razorpay merchant accounts are configured on the server yet.');
        }
      } catch (error) {
        if (isMounted) {
          setStatusType('error');
          setStatusMessage(error.message || 'Unable to load payment account settings.');
        }
      } finally {
        if (isMounted) {
          setIsConfigLoading(false);
        }
      }
    };

    loadPaymentConfig();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    const handleWindowError = (event) => {
      if (!isProcessingRef.current || paymentOutcomeHandledRef.current) {
        return;
      }

      const message = getErrorMessage(event?.error || event, 'An unexpected error occurred during payment.');
      const activeRecord = activePaymentRecordRef.current;

      if (!activeRecord) {
        setIsLoading(false);
        setIsProcessing(false);
        setStatusType('error');
        setStatusMessage(message);
        return;
      }

      paymentOutcomeHandledRef.current = true;
      void markPaymentFailed({
        paymentId: activeRecord.id,
        razorpayOrderId: activeRecord.razorpayOrderId || '',
        failureReason: message
      }).catch((failureSaveError) => {
        console.error('Failed to mark unexpected payment error:', failureSaveError);
      });

      setIsLoading(false);
      setIsProcessing(false);
      setStatusType('error');
      setStatusMessage(message);
      navigate('/payment/failure', {
        replace: true,
        state: {
          error: message,
          paymentRecord: activeRecord
        }
      });
    };

    const handleUnhandledRejection = (event) => {
      if (!isProcessingRef.current || paymentOutcomeHandledRef.current) {
        return;
      }

      const message = getErrorMessage(event?.reason, 'An unexpected payment error occurred.');
      const activeRecord = activePaymentRecordRef.current;

      if (!activeRecord) {
        setIsLoading(false);
        setIsProcessing(false);
        setStatusType('error');
        setStatusMessage(message);
        return;
      }

      paymentOutcomeHandledRef.current = true;
      void markPaymentFailed({
        paymentId: activeRecord.id,
        razorpayOrderId: activeRecord.razorpayOrderId || '',
        failureReason: message
      }).catch((failureSaveError) => {
        console.error('Failed to mark unexpected payment rejection:', failureSaveError);
      });

      setIsLoading(false);
      setIsProcessing(false);
      setStatusType('error');
      setStatusMessage(message);
      navigate('/payment/failure', {
        replace: true,
        state: {
          error: message,
          paymentRecord: activeRecord
        }
      });
    };

    window.addEventListener('error', handleWindowError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    return () => {
      window.removeEventListener('error', handleWindowError);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, [navigate]);

  const amountDisplay = useMemo(() => {
    const parsedAmount = Number(formData.amount);
    if (!Number.isFinite(parsedAmount) || parsedAmount <= 0) {
      return '0.00';
    }
    return parsedAmount.toFixed(2);
  }, [formData.amount]);

  const validateForm = () => {
    const nextErrors = {};

    if (!formData.customerName.trim()) {
      nextErrors.customerName = 'Full name is required.';
    }

    if (!formData.email.trim()) {
      nextErrors.email = 'Email is required.';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) {
      nextErrors.email = 'Please enter a valid email address.';
    }

    if (!formData.phone.trim()) {
      nextErrors.phone = 'Phone number is required.';
    } else if (!/^[0-9+\-()\s]{7,20}$/.test(formData.phone.trim())) {
      nextErrors.phone = 'Please enter a valid phone number.';
    }

    const amountValue = Number(formData.amount);
    if (!formData.amount.toString().trim()) {
      nextErrors.amount = 'Amount is required.';
    } else if (!Number.isFinite(amountValue) || amountValue <= 0) {
      nextErrors.amount = 'Amount must be greater than zero.';
    }

    if (!formData.merchantAccountId.trim()) {
      nextErrors.merchantAccountId = 'Please choose a Razorpay merchant account.';
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    const nextValue = name === 'merchantAccountId' && value && !paymentConfig.merchantAccounts.some((account) => account.id === value)
      ? paymentConfig.defaultMerchantAccountId || paymentConfig.merchantAccounts[0]?.id || ''
      : value;

    setFormData((previous) => ({
      ...previous,
      [name]: nextValue
    }));

    setErrors((previous) => ({
      ...previous,
      [name]: ''
    }));
  };

  const openCheckout = async (orderResponse, paymentRecord) => {
    const loaded = await loadRazorpayScript();

    if (!loaded || !window.Razorpay) {
      throw new Error('Razorpay checkout failed to initialize.');
    }

    const order = orderResponse?.data?.order;

    if (!order || !paymentRecord?.id) {
      throw new Error('Payment order response is incomplete.');
    }

    activePaymentRecordRef.current = paymentRecord;

    const finalizeFailure = async (reason, failurePayload = {}, extraState = {}) => {
      if (paymentOutcomeHandledRef.current) {
        return;
      }

      paymentOutcomeHandledRef.current = true;

      try {
        await markPaymentFailed({
          paymentId: paymentRecord.id,
          razorpayOrderId: failurePayload.razorpayOrderId || paymentRecord.razorpayOrderId || order.id,
          razorpayPaymentId: failurePayload.razorpayPaymentId || '',
          failureReason: reason
        });
      } catch (failureSaveError) {
        console.error('Failed to mark payment as failed:', failureSaveError);
      }

      if (!isMountedRef.current) {
        return;
      }

      setIsLoading(false);
      setIsProcessing(false);
      setStatusType('error');
      setStatusMessage(reason);
      navigate('/payment/failure', {
        replace: true,
        state: {
          error: reason,
          paymentRecord,
          ...extraState
        }
      });
    };

    const options = {
      key: process.env.REACT_APP_RAZORPAY_KEY_ID || orderResponse.data.razorpayKeyId,
      amount: order.amount,
      currency: order.currency,
      name: 'Race IT Solution Payment Portal',
      description: formData.purpose || 'Corporate training and professional services payment',
      order_id: order.id,
      prefill: {
        name: paymentRecord.customerName,
        email: paymentRecord.email,
        contact: paymentRecord.phone
      },
      notes: {
        paymentRecordId: paymentRecord.id,
        purpose: paymentRecord.purpose || ''
      },
      theme: {
        color: '#7c3aed'
      },
      handler: async (response) => {
        try {
          const verification = await verifyPayment({
            razorpayOrderId: response.razorpay_order_id,
            razorpayPaymentId: response.razorpay_payment_id,
            razorpaySignature: response.razorpay_signature
          });

          paymentOutcomeHandledRef.current = true;
          setStatusType('success');
          setStatusMessage('Payment verified successfully. Redirecting to the success page...');
          setIsLoading(false);
          setIsProcessing(false);

          navigate('/payment/success', {
            replace: true,
            state: {
              payment: verification.data,
              amount: paymentRecord.amount,
              purpose: paymentRecord.purpose
            }
          });
        } catch (verificationError) {
          await finalizeFailure(
            getErrorMessage(verificationError, 'Payment verification failed.'),
            {
              razorpayOrderId: response.razorpay_order_id,
              razorpayPaymentId: response.razorpay_payment_id
            }
          );
        }
      },
      modal: {
        ondismiss: async () => {
          await finalizeFailure('Payment popup closed. You can try again when ready.', {
            razorpayOrderId: paymentRecord.razorpayOrderId || order.id
          });
        }
      }
    };

    const razorpay = new window.Razorpay(options);
    razorpay.on('payment.failed', (response) => {
      void finalizeFailure(
        response?.error?.description || 'Payment failed. Please try again.',
        {
          razorpayOrderId: response?.error?.metadata?.order_id || paymentRecord.razorpayOrderId || order.id,
          razorpayPaymentId: response?.error?.metadata?.payment_id || ''
        },
        {
          code: response?.error?.code,
          source: response?.error?.source
        }
      );
    });

    razorpay.open();
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    setIsProcessing(true);
    paymentOutcomeHandledRef.current = false;
    activePaymentRecordRef.current = null;
    setStatusMessage('');
    setStatusType('info');

    let orderResponse = null;

    try {
      orderResponse = await createPaymentOrder({
        customerName: formData.customerName.trim(),
        email: formData.email.trim(),
        phone: formData.phone.trim(),
        amount: Number(formData.amount),
        purpose: formData.purpose.trim(),
        merchantAccountId: formData.merchantAccountId.trim()
      });

      await openCheckout(orderResponse, orderResponse.data.payment);
    } catch (error) {
      if (orderResponse?.data?.payment?.id && !paymentOutcomeHandledRef.current) {
        try {
          await markPaymentFailed({
            paymentId: orderResponse.data.payment.id,
            razorpayOrderId: orderResponse.data.payment.razorpayOrderId || orderResponse.data.order?.id || '',
            failureReason: getErrorMessage(error, 'Unable to initiate payment.')
          });
        } catch (failureSaveError) {
          console.error('Failed to mark payment initiation error:', failureSaveError);
        }
      }

      setIsLoading(false);
      setIsProcessing(false);
      setStatusType('error');
      setStatusMessage(getErrorMessage(error, 'Unable to initiate payment.'));
    }
  };

  return (
    <div className="min-h-[100dvh] w-screen bg-[linear-gradient(135deg,#f8f4fb,#fff8f3)] px-3 pt-3 pb-4 sm:px-4 sm:pt-4 sm:pb-5 lg:px-6 lg:pt-6 lg:pb-6">
      <div className="mx-auto grid min-h-[calc(100dvh-1.75rem)] w-full max-w-6xl grid-rows-[auto_minmax(0,1fr)] overflow-hidden rounded-none bg-white shadow-2xl ring-1 ring-black/5 lg:grid-cols-[1.08fr_0.92fr] lg:grid-rows-1 lg:rounded-3xl">
        <div className="bg-gradient-to-br from-[#6d28d9] via-[#8b5cf6] to-[#f97316] px-5 py-4 text-white sm:px-6 sm:py-5 lg:px-8 lg:py-8">
          <div className="inline-flex items-center rounded-full bg-white/15 px-3 py-1.5 text-[11px] font-medium backdrop-blur sm:px-4 sm:py-2 sm:text-xs">
            Secure Payment Portal
          </div>
          <h1 className="mt-3 max-w-2xl text-2xl font-bold leading-tight sm:mt-4 sm:text-3xl lg:text-4xl">
            Corporate Training &amp; Professional Services Payment
          </h1>
          <p className="mt-2 max-w-2xl text-xs leading-5 text-white/90 sm:mt-3 sm:text-sm sm:leading-6 lg:text-base">
            Complete your payment securely using Razorpay. After payment confirmation, our team will process your training, consulting, workshop, or service request and share the required details promptly.
          </p>

          <div className="mt-3 grid gap-2 sm:mt-4 sm:grid-cols-2 sm:gap-3">
            <FeatureCard
              title="100% Secure"
              description="All payments are processed through Razorpay's secure and trusted payment infrastructure."
            />
            <FeatureCard
              title="Instant Confirmation"
              description="Receive immediate payment confirmation once your transaction is successfully completed."
            />
            <FeatureCard
              title="Multiple Payment Options"
              description="Pay conveniently using UPI, Credit Cards, Debit Cards, Net Banking, and Wallets."
            />
            <FeatureCard
              title="Professional Support"
              description="Our team is available to assist you throughout the engagement and payment process."
            />
          </div>
        </div>

        <div className="flex h-full min-h-0 flex-col overflow-hidden bg-white px-5 py-4 sm:px-6 sm:py-5 lg:px-8 lg:py-8">
          <div className="shrink-0">
            <h2 className="text-xl font-bold tracking-tight text-gray-900 sm:text-2xl">
              Complete Your Payment
            </h2>
            <p className="mt-1.5 text-xs leading-5 text-gray-600 sm:text-sm sm:leading-6">
              Enter your details below to proceed with a secure payment.
            </p>
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto pb-4 pr-1 sm:pb-5">
            {statusMessage && (
              <div
                className={`mt-3 rounded-2xl border px-3 py-2 text-xs sm:px-4 sm:py-3 sm:text-sm ${
                  statusType === 'success'
                    ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
                    : statusType === 'error'
                      ? 'border-red-200 bg-red-50 text-red-700'
                      : 'border-blue-200 bg-blue-50 text-blue-700'
                }`}
              >
                {statusMessage}
              </div>
            )}

            <form onSubmit={handleSubmit} className="mt-3 space-y-3 sm:mt-4 sm:space-y-4">
              <div className="grid gap-3 sm:grid-cols-2 sm:gap-4">
                <div>
                  <label className="mb-1.5 block text-xs font-medium text-gray-700 sm:text-sm" htmlFor="customerName">
                    Full Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="customerName"
                    name="customerName"
                    value={formData.customerName}
                    onChange={handleChange}
                    className="w-full rounded-xl border border-gray-300 px-3 py-2.5 text-sm outline-none transition focus:border-purple-500 focus:ring-4 focus:ring-purple-100 sm:px-4"
                    placeholder="Enter full name"
                    autoComplete="name"
                  />
                  {errors.customerName && <p className="mt-1.5 text-[11px] text-red-600 sm:text-xs">{errors.customerName}</p>}
                </div>

                <div>
                  <label className="mb-1.5 block text-xs font-medium text-gray-700 sm:text-sm" htmlFor="email">
                    Email <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full rounded-xl border border-gray-300 px-3 py-2.5 text-sm outline-none transition focus:border-purple-500 focus:ring-4 focus:ring-purple-100 sm:px-4"
                    placeholder="Enter email address"
                    autoComplete="email"
                  />
                  {errors.email && <p className="mt-1.5 text-[11px] text-red-600 sm:text-xs">{errors.email}</p>}
                </div>
              </div>

              <div className="grid gap-3 sm:grid-cols-2 sm:gap-4">
              <div>
                <label className="mb-1.5 block text-xs font-medium text-gray-700 sm:text-sm" htmlFor="phone">
                  Phone Number <span className="text-red-500">*</span>
                </label>
                  <input
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="w-full rounded-xl border border-gray-300 px-3 py-2.5 text-sm outline-none transition focus:border-purple-500 focus:ring-4 focus:ring-purple-100 sm:px-4"
                    placeholder="Enter phone number"
                    autoComplete="tel"
                  />
                  {errors.phone && <p className="mt-1.5 text-[11px] text-red-600 sm:text-xs">{errors.phone}</p>}
                </div>

                <div>
                  <label className="mb-1.5 block text-xs font-medium text-gray-700 sm:text-sm" htmlFor="amount">
                    Amount (INR) <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="amount"
                    name="amount"
                    type="number"
                    min="1"
                    step="0.01"
                    value={formData.amount}
                    onChange={handleChange}
                    className="w-full rounded-xl border border-gray-300 px-3 py-2.5 text-sm outline-none transition focus:border-purple-500 focus:ring-4 focus:ring-purple-100 sm:px-4"
                    placeholder="0.00"
                  />
                  {errors.amount && <p className="mt-1.5 text-[11px] text-red-600 sm:text-xs">{errors.amount}</p>}
                </div>
              </div>

              <div>
                <label className="mb-1.5 block text-xs font-medium text-gray-700 sm:text-sm" htmlFor="merchantAccountId">
                  Razorpay Merchant Account <span className="text-red-500">*</span>
                </label>
                <CustomSelect
                  id="merchantAccountId"
                  name="merchantAccountId"
                  value={formData.merchantAccountId}
                  onChange={(event) => {
                    handleChange(event);
                  }}
                  placeholder={isConfigLoading ? 'Loading accounts...' : 'Choose an account'}
                  options={paymentConfig.merchantAccounts.map((account) => ({
                    value: account.id,
                    label: account.isDefault ? `${account.label} (default)` : account.label
                  }))}
                  disabled={isConfigLoading || !paymentConfig.merchantAccounts.length}
                  buttonClassName="rounded-md border-gray-300 bg-white px-3 py-2.5"
                />
                <p className="mt-1.5 text-[11px] text-gray-500 sm:text-xs">
                  Payments will be created and verified using the selected Razorpay merchant account.
                </p>
                {errors.merchantAccountId && (
                  <p className="mt-1.5 text-[11px] text-red-600 sm:text-xs">{errors.merchantAccountId}</p>
                )}
                {!paymentConfig.merchantAccounts.length && !isConfigLoading && (
                  <p className="mt-2 text-[11px] text-amber-600 sm:text-xs">
                    No merchant accounts are configured yet.
                  </p>
                )}
              </div>

              <div>
                <label className="mb-1.5 block text-xs font-medium text-gray-700 sm:text-sm" htmlFor="purpose">
                  Purpose / Payment Description
                </label>
                <textarea
                  id="purpose"
                  name="purpose"
                  rows="3"
                  value={formData.purpose}
                  onChange={handleChange}
                  className="w-full rounded-xl border border-gray-300 px-3 py-2.5 text-sm outline-none transition focus:border-purple-500 focus:ring-4 focus:ring-purple-100 sm:px-4"
                  placeholder="Example: Corporate Training, Consulting, Workshop Registration, Service Payment"
                />
              </div>

              <div className="rounded-2xl border border-purple-100 bg-[linear-gradient(135deg,#faf5ff,#fff7ed)] p-3 sm:p-4">
                <div className="flex items-center justify-between gap-4">
                  <span className="text-xs font-medium text-gray-700 sm:text-sm">Total Payable Amount</span>
                  <span className="text-xl font-bold text-gray-900 sm:text-2xl">₹{amountDisplay}</span>
                </div>
              </div>

              <button
                type="submit"
                disabled={isLoading || isProcessing || isConfigLoading}
                className="flex w-full items-center justify-center rounded-xl bg-gradient-to-r from-purple-600 to-orange-500 px-5 py-3 text-sm font-semibold text-white shadow-lg transition hover:opacity-95 disabled:cursor-not-allowed disabled:opacity-60 sm:py-3.5 sm:text-base"
              >
                {isLoading || isProcessing
                  ? 'Processing payment...'
                  : isConfigLoading
                    ? 'Loading payment accounts...'
                    : 'Pay securely with Razorpay'}
              </button>

              <p className="pt-0.5 text-center text-[11px] text-gray-500 sm:text-xs">
                Payments are securely processed via Razorpay.
              </p>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentPage;
